import Icon from '@ant-design/icons';
import { Button, Col, Divider, Dropdown, Menu, Row, Select, Space } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import hubApi from '../../../api/HubApi';
import locationApi from '../../../api/LocationApi';
import projectApi from '../../../api/ProjectApi';
import projectIssueApi from '../../../api/ProjectIssueApi';
import projectIssueRootCauseApi from '../../../api/ProjectIssueRootCauseApi';
import projectIssueTypeApi from '../../../api/ProjectIssueTypeApi';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import SelectTableComponent from '../../../components/SelectTableComponent/SelectTableComponent';
import CustomContext from '../../../context/CustomContext';
import { Page } from '../../../model/elements';
import {
    Hub,
    Location,
    Project,
    ProjectIssue,
    ProjectIssueRootCause,
    ProjectIssueSubtype,
    ProjectIssueType,
} from '../../../model/entities';
import { ProjectIssueStatus, projectIssueStatuses } from '../../../model/types';
import { ReactComponent as CaretDownSvg } from '../../../resources/images/caret-down.svg';
import { ReactComponent as CloudUploadSvg } from '../../../resources/images/cloud-upload.svg';
import notificationService from '../../../services/NotificationService';
import stringService from '../../../services/StringService';
import tableService from '../../../services/TableService';
import CopyProjectIssuesModal from './CopyProjectIssuesModal/CopyProjectIssuesModal';
import styles from './ProjectIssuesPage.module.scss';
import ProjectIssueStatusComponent from './ProjectIssueStatusComponent/ProjectIssueStatusComponent';

class ProjectIssuesPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            hubs: [],
            projects: [],
            types: [],
            subtypes: [],
            rootCauses: [],
            locations: [],
            selectedRowKeys: [],
            selectedProjectIssues: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            // list hubs
            this.setState({ loading: 'loading' });
            const hubs = await hubApi.list(true);
            this.setState({ hubs });

            // list projects
            if (hubs.length > 0) {
                const hub = hubs[0];
                const responses = await Promise.all([projectApi.list(hub.provider, hub.id)]);
                const projects = responses[0];
                this.setState({ hub, projects });
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    list = async (hub?: Hub, project?: Project, status?: ProjectIssueStatus, typeId?: string, subtypeId?: string) => {
        const { projectIssuesPage, searchText } = this.state;

        // list project issues
        const page = 0;
        const size = projectIssuesPage?.size || tableService.pageSize;
        const sortField = projectIssuesPage?.sort.field || 'identifier';
        const sortOrder = projectIssuesPage?.sort.order || false;
        await this.listProjectIssues(
            page,
            size,
            sortField,
            sortOrder,
            hub,
            project,
            status,
            typeId,
            subtypeId,
            searchText,
        );
    };

    search = async (searchText: string) => {
        const { projectIssuesPage, hub, project, status, typeId, subtypeId } = this.state;
        try {
            this.setState({ loading: 'loading', searchText });
            const page = 0;
            const size = projectIssuesPage!.size;
            const sortField = projectIssuesPage!.sort.field!;
            const sortOrder = projectIssuesPage!.sort.order!;
            await this.listProjectIssues(
                page,
                size,
                sortField,
                sortOrder,
                hub,
                project,
                status,
                typeId,
                subtypeId,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { hub, project, status, typeId, subtypeId, searchText } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const page = pagination.current! - 1;
            const size = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            await this.listProjectIssues(
                page,
                size,
                sortField,
                sortOrder,
                hub,
                project,
                status,
                typeId,
                subtypeId,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    listProjectIssues = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        hub?: Hub,
        project?: Project,
        status?: ProjectIssueStatus,
        typeId?: string,
        subtypeId?: string,
        searchText?: string,
    ) => {
        let projectIssuesPage: Page<ProjectIssue> | undefined;
        if (hub && project) {
            projectIssuesPage = await projectIssueApi.list(
                page,
                size,
                sortField,
                sortOrder,
                hub.id,
                project.id!,
                status,
                typeId,
                subtypeId,
                searchText,
            );
        }
        this.setState({ projectIssuesPage });
    };

    selectHub = async (hubId: string | undefined) => {
        const { hubs } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const hub = hubs.find((h) => h.id === hubId);
            const projects = hub ? await projectApi.list(hub.provider, hub.id) : [];
            const project = undefined;
            const status = undefined;
            const typeId = undefined;
            const subtypeId = undefined;
            const projectIssuesPage = undefined;
            this.setState({ projects, hub, project, status, typeId, subtypeId, projectIssuesPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    selectProject = async (projectId?: string) => {
        const { hub, projects } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const project = projects.find((p) => p.id === projectId);
            let types: ProjectIssueType[] = [];
            let rootCauses: ProjectIssueRootCause[] = [];
            let locations: Location[] = [];
            if (project) {
                const responses = await Promise.all([
                    projectIssueTypeApi.list(hub!.id, project.id),
                    projectIssueRootCauseApi.list(hub!.id, project.id),
                    locationApi.list(hub!.id, project.id),
                ]);
                types = responses[0];
                rootCauses = responses[1];
                locations = responses[2];
            }
            const status = undefined;
            const typeId = undefined;
            const subtypeId = undefined;
            this.unselectAll();
            this.setState({ project, types, rootCauses, locations, status, typeId, subtypeId });

            await this.list(hub, project);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    selectStatus = async (status?: ProjectIssueStatus) => {
        const { hub, project, typeId, subtypeId } = this.state;
        try {
            this.setState({ loading: 'loading', status });

            await this.list(hub, project, status, typeId, subtypeId);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    selectType = async (typeId?: string) => {
        const { hub, project, status, types } = this.state;
        try {
            const subtypes = typeId ? types.find((t) => t.id === typeId)!.subtypes : [];
            const subtypeId = undefined;
            this.setState({ loading: 'loading', status, typeId, subtypeId, subtypes });

            await this.list(hub, project, status, typeId);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    selectSubtype = async (subtypeId?: string) => {
        const { hub, project, status, typeId } = this.state;
        try {
            this.setState({ loading: 'loading', status, subtypeId });

            await this.list(hub, project, status, typeId, subtypeId);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    select = (selectedRowKeys: Key[], selectedRows: ProjectIssue[]) => {
        this.setState({ selectedRowKeys, selectedProjectIssues: selectedRows });
    };

    unselectAll = () => {
        this.setState({ selectedRowKeys: [], selectedProjectIssues: [] });
    };

    isCopyButtonDisabled = (): boolean => {
        return this.state.selectedProjectIssues.length === 0;
    };

    showCopyModal = (copyIssuesModalVisible: boolean) => {
        this.setState({ copyIssuesModalVisible });
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSelector()}
                {this.renderToolbar()}
                {this.renderTable()}
                {this.renderCopyIssuesModal()}
            </>
        );
    };

    renderSelector = (): React.ReactElement | undefined => {
        const { hubs, hub, projects, project, types, subtypes, typeId, subtypeId, status } = this.state;
        const hubOptions = hubs
            .sort((a, b) => stringService.sort(a.name, b.name))
            .map((hub) => (
                <Select.Option key={hub.id} value={hub.id}>
                    {`${hub.provider} > ${hub.name}`}
                </Select.Option>
            ));
        const projectOptions = projects
            .sort((a, b) => stringService.sort(a.name, b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));
        const statusOptions = projectIssueStatuses.map((status) => (
            <Select.Option key={status} value={status}>
                <FormattedMessage id={`projectIssue.status.${status}`} />
            </Select.Option>
        ));
        const typeOptions = types
            .sort((a, b) => stringService.sort(a.title, b.title))
            .map((type) => (
                <Select.Option key={type.id} value={type.id}>
                    {type.title}
                </Select.Option>
            ));
        const subtypeOptions = subtypes
            .sort((a, b) => stringService.sort(a.title, b.title))
            .map((subtype) => (
                <Select.Option key={subtype.id} value={subtype.id}>
                    {subtype.title}
                </Select.Option>
            ));

        return (
            <div className={styles.selectors}>
                <Row gutter={[28, 0]}>
                    <Col span={6} className={styles.selector}>
                        <label>
                            <FormattedMessage id="projectIssues.hub" />
                        </label>
                        <Select
                            size="large"
                            className={styles.hubs}
                            value={hub?.id}
                            onChange={this.selectHub}
                            placeholder={<FormattedMessage id="projectIssues.hub.placeholder" />}
                        >
                            {hubOptions}
                        </Select>
                    </Col>
                    <Col span={6} className={styles.selector}>
                        <label>
                            <FormattedMessage id="projectIssues.projects" />
                        </label>
                        <Select
                            size="large"
                            allowClear
                            className={styles.projects}
                            value={project?.id}
                            onChange={this.selectProject}
                            placeholder={<FormattedMessage id="projectIssues.projects.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                        >
                            {projectOptions}
                        </Select>
                    </Col>
                    <Col span={4} className={styles.selector}>
                        <label>
                            <FormattedMessage id="projectIssues.status" />
                        </label>
                        <Select
                            size="large"
                            allowClear
                            className={styles.statuses}
                            value={status}
                            onChange={this.selectStatus}
                            placeholder={<FormattedMessage id="projectIssues.status.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                        >
                            {statusOptions}
                        </Select>
                    </Col>
                    <Col span={4} className={styles.selector}>
                        <label>
                            <FormattedMessage id="projectIssues.types" />
                        </label>
                        <Select
                            size="large"
                            allowClear
                            className={styles.types}
                            value={typeId}
                            onChange={this.selectType}
                            placeholder={<FormattedMessage id="projectIssues.types.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                        >
                            {typeOptions}
                        </Select>
                    </Col>
                    <Col span={4} className={styles.selector}>
                        <label>
                            <FormattedMessage id="projectIssues.subtypes" />
                        </label>
                        <Select
                            size="large"
                            allowClear
                            className={styles.subtypes}
                            value={subtypeId}
                            onChange={this.selectSubtype}
                            placeholder={<FormattedMessage id="projectIssues.subtypes.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                        >
                            {subtypeOptions}
                        </Select>
                    </Col>
                    <Divider />
                </Row>
            </div>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const { project } = this.state;

        return (
            <div className="toolbar">
                <Search
                    placeholder={intl.formatMessage({ id: 'projectIssues.search' })}
                    onSearch={this.search}
                    size="large"
                    className="search"
                    disabled={!project}
                />
                <Space>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={!project}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="copy"
                                    icon={<Icon component={CloudUploadSvg} />}
                                    onClick={() => this.showCopyModal(true)}
                                    disabled={this.isCopyButtonDisabled()}
                                >
                                    <FormattedMessage id="projectIssues.copy" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderCopyIssuesModal = (): React.ReactElement | undefined => {
        const { copyIssuesModalVisible, hub, project, projects, selectedProjectIssues } = this.state;

        if (hub && project && copyIssuesModalVisible) {
            return (
                <CopyProjectIssuesModal
                    hub={hub}
                    sourceProject={project}
                    projects={projects}
                    selectedProjectIssues={selectedProjectIssues}
                    onUpdate={() => this.showCopyModal(false)}
                    onCancel={() => this.showCopyModal(false)}
                />
            );
        }
    };

    renderTable = (): React.ReactElement | undefined => {
        const { projectIssuesPage, selectedProjectIssues, selectedRowKeys, types, rootCauses, locations, loading } =
            this.state;
        const items = projectIssuesPage ? projectIssuesPage.content : [];
        const columns: ColumnsType<ProjectIssue> = [
            {
                title: <FormattedMessage id="projectIssue.identifier" />,
                dataIndex: 'identifier',
                key: 'identifier',
                sorter: true,
                defaultSortOrder: 'descend',
                width: 50,
                align: 'right',
                render: (value: number) => value,
            },
            {
                title: <FormattedMessage id="projectIssue.status" />,
                dataIndex: 'status',
                key: 'status',
                sorter: true,
                width: 190,
                align: 'center',
                render: (value: ProjectIssueStatus) => <ProjectIssueStatusComponent status={value} />,
            },
            {
                title: <FormattedMessage id="projectIssue.ngIssueTypeId" />,
                dataIndex: 'ngIssueTypeId',
                key: 'ngIssueTypeId',
                width: 190,
                render: (value: string) => types.find((p) => p.id === value)?.title,
            },
            {
                title: <FormattedMessage id="projectIssue.ngIssueSubtypeId" />,
                dataIndex: 'ngIssueSubtypeId',
                key: 'ngIssueSubtypeId',
                width: 190,
                render: (value: string) => types.flatMap((p) => p.subtypes).find((p) => p.id === value)?.title,
            },
            {
                title: <FormattedMessage id="projectIssue.rootCauseId" />,
                dataIndex: 'rootCauseId',
                key: 'rootCauseId',
                width: 190,
                render: (value: string) => rootCauses.find((r) => r.id === value)?.title,
            },
            {
                title: <FormattedMessage id="projectIssue.lbsLocation" />,
                dataIndex: 'lbsLocation',
                key: 'lbsLocation',
                width: 190,
                render: (value: string) => locations.find((r) => r.id === value)?.path.join(' > '),
            },
            {
                title: <FormattedMessage id="projectIssue.title" />,
                dataIndex: 'title',
                key: 'title',
                sorter: true,
                render: (value: string) => value,
            },
        ];

        let rowSelection: TableRowSelection<ProjectIssue> | undefined;
        rowSelection = {
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: this.select,
        };

        return (
            <SelectTableComponent
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(projectIssuesPage)}
                rowKey="id"
                onChange={this.paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
                rowSelection={rowSelection}
                selectedItems={selectedProjectIssues.map((projectIssue) => projectIssue.identifier)}
                onUnselectAll={this.unselectAll}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="projectIssues">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(ProjectIssuesPage);

interface Props extends WrappedComponentProps, RouteComponentProps {}

interface State {
    hubs: Hub[];
    hub?: Hub;
    projects: Project[];
    project?: Project;
    types: ProjectIssueType[];
    subtypes: ProjectIssueSubtype[];
    rootCauses: ProjectIssueRootCause[];
    locations: Location[];
    status?: ProjectIssueStatus;
    typeId?: string;
    subtypeId?: string;
    projectIssuesPage?: Page<ProjectIssue>;
    searchText?: string;
    loading?: 'loading' | 'saving';
    selectedRowKeys: Key[];
    selectedProjectIssues: ProjectIssue[];
    copyIssuesModalVisible?: boolean;
}
